import {
  Banner,
  Button,
  Checkbox,
  RadioButton,
  TextField,
  TextStyle,
  TimezoneSelect
} from '@superops/ui-design-system';
import { Store } from '@styled-icons/fa-solid';
import {
  getTranslatedText,
  CustomModal,
  B1,
  Loader,
  TEXT_LENGTH,
  H3,
  reactSelectInlineError,
  T2,
  toast,
  validateForm,
  preFormatForm,
  clientConstraints,
  trimInputValues,
  PSA_CONSTANTS,
  AppContext,
  useProduct,
  COMMON_CONSTANTS,
  getLokaliseKeyByProduct
} from '@nile/components';
import { useMutation } from '@apollo/client';
import { UserSelect } from '@nile/mangifera/src/Components/Ticket';
import { Tooltip } from 'react-clone-tippy';
import {
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/banyan/src/Pebbles/select';
import { reactSelectErrorStyles } from '@nile/banyan/src/Utils/style';
import React, { useState, useCallback, useEffect, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import ClientSiteSelection from '@nile/client_management/src/Pages/Invoice/ClientSiteSelection';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import { ErrorBoundary } from 'react-error-boundary';
import withErrorBoundary from '@nile/components/src/HOC/withErrorBoundary';
import {
  AccountClient,
  CHECK_USER_EMAIL,
  CREATE_CLIENT,
  GET_BASIC_CLIENT_DATA
} from '@nile/graphql/src';

const ModalWrapper = styled.div`
  min-width: 700px;
  max-width: 1000px;
  background: #ffffff;

  .title {
    font-size: 20px;
    line-height: 21px;
  }

  .content {
    overflow: auto;
    max-height: 70vh;
  }

  .Superops-InlineError {
    color: #bf0711;
  }

  .Superops-TextField--error > .Superops-TextField__Input {
    &:hover,
    &:focus,
    &:focus:hover {
      border-bottom: 0.1rem solid var(--sops-border-hover);
    }
  }
`;

const ClientSiteContainer = styled.div`
  .client-site-selection {
    display: flex;
    gap: 20px;
  }
  .site-select .Polaris-Labelled__LabelWrapper {
    margin: 0 !important;
  }
`;

const TimezoneSelectContainer = styled.div`
  .value_selector__control {
    border-bottom: 1px solid #00000033 !important;
  }
`;

const AssociateButton = (props) => {
  const { button, showButtonText = false, isBulkUpdate = false } = props;
  const intl = useIntl();
  const currentProduct = useProduct();
  const [modalState, setModalState] = useState(false);
  const toggleModal = useCallback(
    () => setModalState((modalState) => !modalState),
    []
  );
  return (
    <>
      <CustomModal open={modalState} onClose={() => setModalState(false)}>
        <AssociateForm {...props} intl={intl} toggleModal={toggleModal} />
      </CustomModal>
      <Tooltip
        title={getTranslatedText(
          intl,
          getLokaliseKeyByProduct(currentProduct, 'associateUnknownTicket')
        )}
        delay={400}
        duration={250}
        position="top-start"
        inertia
        trigger="mouseenter"
        animation="shift"
        animateFill={false}
      >
        {button ? (
          <div onClick={toggleModal}>{button}</div>
        ) : showButtonText ? (
          <Button
            onClick={toggleModal}
            icon={<SVGIcon name="RightArrowCircle" color="#4B8413" />}
          >
            <span>
              <FormattedMessage id="ticket.unknown.associate.client" />
            </span>
          </Button>
        ) : (
          // handling this separate since designsystem creates empty space if contents empty

          <Button
            onClick={toggleModal}
            icon={<SVGIcon name="RightArrowCircle" color="#4B8413" />}
          />
        )}
      </Tooltip>
    </>
  );
};

const AssociateForm = (props) => {
  const {
    intl,
    title,
    name,
    requester,
    meta,
    onSubmit,
    loading,
    toggleModal
  } = props;

  const appContext = useContext(AppContext);
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const currentProduct = useProduct();
  const { company } = appContext;
  const [createNewClient, setCreateNewClient] = useState(false);
  const [useFromAddress, setUseFromAddress] = useState(false);
  const [selectedClient, setSelectedClient] = useState({
    client: null,
    site: null,
    requester: null
  });
  const [showRequester, setShowRequester] = useState(false);
  const [client, setClient] = useState({
    primaryContact: {
      email: meta?.fromAddress || ''
    },
    site: {
      timezoneCode: company?.hqSite?.timezoneCode
    }
  });

  const [submitActionLoading, setSubmitActionLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [emailValidationError, setEmailValidationError] = useState(null);
  const [errorAnimation, setErrorAnimation] = useState(false);
  const handleChange = (_checked, newValue) => {
    if (_checked) {
      updateReqField(null);
      setUseFromAddress(true);
    } else {
      setUseFromAddress(false);
    }
  };

  function updateClientProperties(args) {
    const properties = {
      ...args.properties
    };
    let updatedObject = {};
    if (isMSPProduct) {
      updatedObject = {
        client: {
          accountId: properties.client.accountId,
          name: properties.client.name
        },
        site: {
          id: properties.site.id,
          name: properties.site.name
        }
      };
    } else {
      updatedObject = {
        site: {
          id: properties.site.id,
          name: properties.site.name
        }
      };
    }
    const updatedClientsArray = {
      ...selectedClient,
      ...updatedObject
    };
    setSelectedClient(updatedClientsArray);
  }

  function updateReqField(val) {
    const updatedClientsArray = {
      ...selectedClient,
      requester: val && val.userId ? val : null
    };
    setSelectedClient(updatedClientsArray);
  }

  function disableAssociate() {
    if (createNewClient) {
      return isEmpty(client?.name);
    }
    if (!isMSPProduct) {
      return selectedClient.site === null;
    }
    return selectedClient.client === null;
  }

  function handleClientUpdate(field, nestedField) {
    return (val) => {
      const clonedClient = cloneDeep(client);
      if (nestedField !== undefined) {
        clonedClient[field] = { ...clonedClient?.[field], [nestedField]: val };
      } else {
        clonedClient[field] = val;
      }
      setClient(clonedClient);
    };
  }

  function prefillSiteName() {
    if (!client?.site?.name && client.name) {
      handleClientUpdate(
        'site',
        'name'
      )(
        `${client.name} ${getTranslatedText(
          intl,
          'client.new.site.prefill.suffix'
        )}`
      );
    }
  }

  const handleValidation = () => {
    let resultValidation = null;
    const clientInfo = {
      ...client
    };
    const validationResult = validateForm(
      preFormatForm(clientInfo),
      clientConstraints
    );
    if (validationResult) {
      resultValidation = {
        ...validationResult
      };
    }

    if (resultValidation) {
      setErrors(resultValidation);
    } else {
      setErrors(null);
    }

    return resultValidation;
  };

  const [createClientAndSubmit, { loading: createClientLoading }] = useMutation(
    CREATE_CLIENT,
    {
      client: AccountClient,
      onCompleted({ createClient }) {
        if (createClient) {
          // toast('success', 'app.toast.client.creation.success');
          setSubmitActionLoading(false);
          const data = {
            client: { accountId: createClient.accountId },
            site: { id: createClient?.hqSite?.id }
          };
          if (
            !isEqual(meta?.fromAddress, createClient?.primaryContact?.email)
          ) {
            data.requester = {
              userId: createClient.primaryContact.userId
            };
          }
          onSubmit(data, toggleModal);
        }
      },
      onError() {
        // toast('error', 'Something went wrong');
        setSubmitActionLoading(false);
      }
    }
  );

  function handleSubmit() {
    const data = {
      site: { id: selectedClient?.site?.id }
    };
    if (isMSPProduct)
      data.client = { accountId: selectedClient.client.accountId };
    if (selectedClient?.requester?.userId) {
      data.requester = { userId: selectedClient.requester.userId };
    }
    if (useFromAddress) {
      data.requester = { email: meta?.fromAddress };
    }
    if (!selectedClient?.requester?.userId && !useFromAddress) {
      data.requester = null;
    }
    onSubmit(data, toggleModal);
  }

  function handleCreateClientAndSubmit() {
    setSubmitActionLoading(true);
    const resultValidation = handleValidation();

    if (resultValidation) {
      setSubmitActionLoading(false);
      return;
    }
    const updatedClient = cloneDeep(client);
    const trimmedForm = trimInputValues(updatedClient);
    if (
      emailValidationError &&
      emailValidationError.status === 'DUPLICATE_EMAIL_ADDRESS'
    ) {
      trimmedForm.primaryContact = {
        ...trimmedForm.primaryContact,
        userId: emailValidationError.userId
      };
    }
    createClientAndSubmit({ variables: trimmedForm, input: trimmedForm });
  }

  function handleAssociate() {
    if (createNewClient) {
      handleCreateClientAndSubmit();
    } else {
      handleSubmit();
    }
  }

  const renderError = (key) => {
    return errors?.[key]?.[0];
  };

  const renderValidationError = () => {
    if (emailValidationError)
      return getTranslatedText(
        intl,
        PSA_CONSTANTS.EMAIL_VALIDATION_ERROR_KEYS[emailValidationError?.status]
      );
    return '';
  };

  const handleClientValidation = () => {
    AccountClient.mutate({
      mutation: CHECK_USER_EMAIL,
      variables: {
        email: client.primaryContact.email,
        accountType: 'CLIENT',
        validateDomain: false
      }
    }).then((res) => {
      if (
        res?.data?.checkUserEmailAddressAvailability?.status !== 'AVAILABLE'
      ) {
        setEmailValidationError(res?.data?.checkUserEmailAddressAvailability);
        setErrorAnimation(true);
        setTimeout(() => {
          setErrorAnimation(false);
        }, 900);
      } else {
        setEmailValidationError(null);
        setErrorAnimation(false);
      }
    });
  };

  useEffect(() => {
    if (!isEqual(selectedClient, props?.selectedClient))
      props?.setSelectedClient && props?.setSelectedClient(selectedClient);
  }, [selectedClient]);

  useEffect(() => {
    if (createNewClient && meta?.fromAddress) {
      handleClientValidation();
    }
  }, [createNewClient]);

  return (
    <ModalWrapper className="br10">
      <div className="header fx actr fx-sb divider p20">
        <div className="fx actr">
          {createNewClient && (
            <div
              className="mr-20 ptr card-border rounded-4px p5 fx actr jctr"
              onClick={() => setCreateNewClient(false)}
            >
              <SVGIcon name="BackArrow" size="sm" />
            </div>
          )}
          <B1 className="title">
            {title ||
              getTranslatedText(
                intl,
                createNewClient
                  ? 'app.common.create.client'
                  : getLokaliseKeyByProduct(
                      currentProduct,
                      'associateUnknownTicket'
                    )
              )}
          </B1>
        </div>
        <span className="ptr" onClick={toggleModal}>
          <SVGIcon name="Close" />
        </span>
      </div>
      <div className="content">
        {!createNewClient ? (
          <div className="p20">
            <ClientSiteContainer>
              <ClientSiteSelection
                site={selectedClient.site}
                client={selectedClient.client}
                clientsToFilter={[]}
                updateProperties={updateClientProperties}
                hideLabel={false}
                hideIcon={false}
                hideSite={selectedClient.client === null && isMSPProduct}
                menuStyleProps={{ height: 'calc(40vh - 20px)' }}
                menuListStyleProps={{ height: 'calc(40vh - 20px)' }}
                clientQuery={GET_BASIC_CLIENT_DATA}
              />
            </ClientSiteContainer>
            {(selectedClient.client?.name ||
              (selectedClient.site?.name && !isMSPProduct)) && (
              <>
                <div className="w280">
                  <p className="pt10 label-color">
                    {' '}
                    <FormattedMessage id="admin.Requesterrole.associate.header" />
                  </p>
                  <UserSelect
                    sortList
                    isClearable
                    fetchBefore
                    client={selectedClient.client}
                    site={selectedClient.site}
                    updateField={(_, val) => updateReqField(val)}
                    property="userId"
                    field="requester"
                    value={requester}
                    defaultValue={name ? [{ name: name, label: name }] : []}
                    icon={Store}
                    isDisabled={useFromAddress}
                    menuStyles={{
                      height: 'calc(40vh - 20px)'
                    }}
                    menuListStyles={{
                      height: 'calc(40vh - 20px)'
                    }}
                  />
                </div>
                <div className="mt20">
                  {loading ? (
                    <Loader />
                  ) : (
                    meta?.fromAddress && (
                      <Checkbox
                        label={
                          `Create '${meta?.fromAddress}` +
                          "' as " +
                          `${
                            isMSPProduct
                              ? selectedClient.client?.name
                              : selectedClient.site?.name
                          }` +
                          ' user'
                        }
                        checked={useFromAddress}
                        id="automatic"
                        name="accounts"
                        onChange={handleChange}
                        disabled={selectedClient?.requester}
                      />
                    )
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="fx fcol g20 p20 pr40 pl40">
            <div onBlur={prefillSiteName}>
              <TextField
                label={getTranslatedText(intl, 'client.new.details.label')}
                placeholder={getTranslatedText(intl, 'client.new.details.pl')}
                value={client?.name}
                onChange={handleClientUpdate('name')}
                error={renderError('name')}
                requiredIndicator
                maxLength={TEXT_LENGTH.size_4}
              />
            </div>
            <div className="fx actr jctr g20">
              <div className="fg1">
                <TextField
                  value={client?.primaryContact?.firstName || ''}
                  label={getTranslatedText(intl, 'client.new.firstname')}
                  placeholder={getTranslatedText(
                    intl,
                    'client.new.firstname.pl'
                  )}
                  type="text"
                  name="firstname"
                  onChange={handleClientUpdate('primaryContact', 'firstName')}
                  error={renderError(`primaryContact.firstName`)}
                  requiredIndicator
                  maxLength={TEXT_LENGTH.size_4}
                />
              </div>
              <div className="fg1">
                <TextField
                  value={client?.primaryContact?.lastName || ''}
                  label={getTranslatedText(intl, 'client.new.lastname')}
                  placeholder={getTranslatedText(
                    intl,
                    'client.new.lastname.pl'
                  )}
                  type="text"
                  name="lastname"
                  onChange={handleClientUpdate('primaryContact', 'lastName')}
                  error={renderError(`primaryContact.lastName`)}
                  maxLength={TEXT_LENGTH.size_4}
                />
              </div>
            </div>
            <div
              className={`f11 ${errorAnimation ? 'shake' : ''}`}
              onBlur={handleClientValidation}
            >
              <TextField
                value={client?.primaryContact?.email || ''}
                label={getTranslatedText(intl, 'client.new.email')}
                placeholder={getTranslatedText(intl, 'client.new.email.pl')}
                type="email"
                name="email"
                onChange={handleClientUpdate('primaryContact', 'email')}
                error={
                  renderError(`primaryContact.email`) || renderValidationError()
                }
                requiredIndicator
                maxLength={TEXT_LENGTH.size_4}
              />
            </div>

            <H3>
              <TextStyle weight="SemiBold">
                {getTranslatedText(intl, 'client.new.h2.siteInfo')}
              </TextStyle>
            </H3>
            <div className="fx actr jctr g20">
              <div className="fx2">
                <TextField
                  value={client?.site?.name}
                  label={getTranslatedText(intl, 'client.new.siteInfo.name')}
                  placeholder={getTranslatedText(
                    intl,
                    'client.new.siteInfo.name.pl'
                  )}
                  type="text"
                  onChange={handleClientUpdate('site', 'name')}
                  error={renderError('site.name')}
                  requiredIndicator
                  maxLength={TEXT_LENGTH.size_4}
                />
              </div>
              <div className="fx2">
                <BusinessHourForm
                  intl={intl}
                  zone={client?.site?.timezoneCode || ''}
                  onChange={handleClientUpdate('site', 'timezoneCode')}
                  // errors={errors}
                />
              </div>
            </div>
            <Banner status="info">
              <p>
                <FormattedMessage
                  id="ticket.unknown.requester.will.be.created"
                  values={{
                    requesterName:
                      client?.primaryContact?.email || meta?.fromAddress
                  }}
                />
              </p>
            </Banner>
          </div>
        )}
      </div>
      <div className="fx p20">
        {!createNewClient && isMSPProduct && (
          <Button
            onClick={() => setCreateNewClient(true)}
            plain
            icon={
              <div className="fx center ignoreOverride">
                <SVGIcon name="Add" />
              </div>
            }
          >
            <FormattedMessage id="ticket.unknown.create.client.and.associate" />
            {/* Create Client */}
          </Button>
        )}
        <span className="mlauto">
          <Button
            onClick={handleAssociate}
            primary
            disabled={disableAssociate()}
            loading={props?.submitLoading || submitActionLoading}
          >
            <FormattedMessage
              id={
                createNewClient
                  ? 'ticket.unknown.create.client.and.associate.btn'
                  : 'ticket.unknown.associate.btn'
              }
            />
          </Button>
        </span>
      </div>
    </ModalWrapper>
  );
};

function BusinessHourForm(props) {
  const { intl, zone, errors } = props;
  const changeTimeZone = (name, opt) => {
    props.onChange(name.value);
  };
  return (
    <TimezoneSelectContainer>
      <T2>
        <div>
          {/* add className="requiredIndicator" to the div when needed */}
          <FormattedMessage id="client.new.business.timezone.pl" />
        </div>
      </T2>
      <ErrorBoundary FallbackComponent={() => <div>Error...</div>}>
        <TimezoneSelect
          value={{ value: zone }}
          onChange={changeTimeZone}
          components={{ IndicatorSeparator, DropdownIndicator }}
          classNamePrefix="value_selector"
          formatGroupLabel={formatGroupLabel}
          timezones={{ ...COMMON_CONSTANTS.TIMEZONES }}
          menuPlacement="auto"
          placeholder={getTranslatedText(
            intl,
            'client.new.business.timezone.label'
          )}
          menuPortalTarget={document.body}
          closeMenuOnScroll={(e) => {
            return (
              e.target &&
              e.target.classList &&
              !(e.target.classList.value.indexOf('menu-list') > -1)
            );
          }}
          styles={
            errors?.['site.timezoneCode']
              ? {
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  ...reactSelectErrorStyles
                }
              : { menuPortal: (base) => ({ ...base, zIndex: 9999 }) }
          }
        />
      </ErrorBoundary>

      {errors?.['site.timezoneCode'] &&
        reactSelectInlineError(errors?.['site.timezoneCode']?.[0])}
    </TimezoneSelectContainer>
  );
}

export default withErrorBoundary(AssociateButton);
